import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Title,
  Text,
  Stack,
  Group,
  Table,
  Card,
  List,
  ThemeIcon,
  rem,
  Badge,
  SimpleGrid,
  Divider,
  Tooltip,
  Loader,
} from "@mantine/core";
import { fetchServerPublicKey } from "../lib/server-keys";
import { PdfDropzone } from "./PdfDropzone";
import {
  IconShieldCheck,
  IconKey,
  IconClock,
  IconScale,
  IconCircleCheckFilled,
  IconX,
  IconLockCheck,
  IconServer,
  IconClockRecord,
  IconCertificate,
  IconBriefcase,
  IconMailCheck,
} from "@tabler/icons-react";
import "./LandingPage.css";
// @ts-ignore
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = "./pdf.worker.js";

interface LandingPageProps {
  onGetStarted: () => void;
  onPdfDetected: (
    file: File,
    hasMetadata: boolean,
    isComplete: boolean,
  ) => void;
}

export function LandingPage({ onGetStarted, onPdfDetected }: LandingPageProps) {
  const [detecting, setDetecting] = useState(false);
  const [serverKey, setServerKey] = useState<{ keyId: string; publicKey: any } | null>(null);

  useEffect(() => {
    fetchServerPublicKey().then(setServerKey);
  }, []);

  const handlePdfDrop = async (file: File) => {
    if (!file || file.type !== "application/pdf") return;

    setDetecting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer.slice(0) })
        .promise;
      const attachments = await pdf.getAttachments();
      const hasMetadata = !!(
        attachments && attachments["signing-metadata.json"]
      );

      let isComplete = false;
      if (hasMetadata && attachments["signing-metadata.json"]) {
        try {
          const content = attachments["signing-metadata.json"].content;
          const decoder = new TextDecoder();
          const jsonStr = decoder.decode(content);
          const metadata = JSON.parse(jsonStr);

          // Check if all signers have signatures
          const signers = metadata.signers || [];
          const signatures = metadata.signatures || [];
          const signedEmails = new Set(
            signatures.map((sig: any) => sig.email?.toLowerCase()),
          );
          const allSignersSigned = signers.every((signer: any) =>
            signedEmails.has(signer.email?.toLowerCase()),
          );
          isComplete = signers.length > 0 && allSignersSigned;
        } catch (e) {
          console.error("Failed to parse metadata:", e);
          // If metadata is invalid, treat as incomplete
          isComplete = false;
        }
      }

      onPdfDetected(file, hasMetadata, isComplete);
    } catch (error) {
      console.error("Failed to analyze PDF:", error);
      // On error, treat as new document (no metadata, not complete)
      onPdfDetected(file, false, false);
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <Container size="lg">
          <Stack gap="xl" align="center" className="hero-content">
            <Title className="hero-title" order={1}>
              Signed+: Your Signature. Your Data. Your Control.
            </Title>
            <Text className="hero-subtitle" size="xl">
              The Digital Ink, Not the Digital Library.{" "}
              <Badge color="blue" variant="light" size="lg" ml="md">
                v0.1.1
              </Badge>
            </Text>
            <Text className="hero-description" size="lg" c="dimmed">
              The world's first zero-storage signing platform. Your document,
              your rules.
            </Text>
            <PdfDropzone
              onFileSelected={handlePdfDrop}
              loading={detecting}
              label="Drop your PDF to get started"
              description="Either to create, sign, or validate, and we'll automatically detect if it's a Signed+ document and get you started"
            />
          </Stack>
        </Container>
      </section>

      {/* TL;DR Section for Non-Technical Users */}
      <section className="tldr-section" style={{ padding: '2rem 0', backgroundColor: 'var(--mantine-color-blue-0)' }}>
        <Container size="md">
          <Card withBorder radius="md" p="xl" bg="white" shadow="sm">
            <Group gap="sm" mb="md">
              <ThemeIcon size="lg" radius="md" color="blue" variant="light">
                <IconBriefcase size={20} />
              </ThemeIcon>
              <Title order={3} c="blue.9">How it works (The Simple Version)</Title>
            </Group>
            <Stack gap="md">
              <div>
                <Text fw={600} size="lg" c="blue.9">1. We don't store your documents.</Text>
                <Text c="dimmed">You drop a PDF in, sign it, and download the finished file. You have the freedom to route your documents via your existing methods (like your own email), so we never see your contracts, medical records, PII, or GDPR-sensitive data.</Text>
              </div>

              <div>
                <Text fw={600} size="lg" c="blue.9">2. You don't need an "account" with us.</Text>
                <Text c="dimmed">You don't need another web profile to manage. Instead, your device securely signs the documents locally. By cutting out the middleman, we can be cheaper and more open than other document signing services, and we can keep the basic tier 100% free forever.</Text>
              </div>

              <div>
                <Text fw={600} size="lg" c="blue.9">3. We don't hold your keys.</Text>
                <Text c="dimmed">Your digital signing keys live safely on your own device, rather than on our servers. Even we can't access them! To make things easy, we provide simple, secure options to back up and restore your keys whenever you need to.</Text>
              </div>

              <div>
                <Text fw={600} size="lg" c="blue.9">4. The document is the database.</Text>
                <Text c="dimmed">All your signing metadata, audit trails, and cryptographic requirements are embedded directly inside the PDF itself. The source of truth always travels entirely inside the file you own and control, meaning anyone can verify the document independently without relying on us.</Text>
              </div>
            </Stack>
          </Card>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Container size="lg">
          <Stack gap="xl" align="center" mb={60}>
            <Title order={1} className="section-title" ta="center">
              Sovereignty & Proof
            </Title>
          </Stack>
          <Stack gap="xl">
            <div className="feature-block">
              <div className="feature-card premium-wrapper">
                <Group gap="md" mb="md">
                  <IconShieldCheck
                    className="section-icon"
                    style={{ width: rem(28), height: rem(28) }}
                  />
                  <Title order={2} className="section-title">
                    Secure by Design: Privacy is Baked In
                  </Title>
                </Group>
                <Stack gap="lg">
                  <div>
                    <Title order={3} mb="sm">
                      No Cloud Accounts. No Password Leaks.
                    </Title>
                    <Text c="dimmed">
                      You aren't signing up for yet another web profile and giving hackers one more place to steal your data from. Instead of creating an account on our servers, you create a secure <strong>local vault</strong> directly on your device (your phone, tablet, or laptop) that we can never access.
                    </Text>
                  </div>

                  <List spacing="md" className="feature-list">
                    <List.Item>
                      <Text fw={600}>Bring Your Own Keys:</Text> Already have a
                      PGP key? Use it.
                    </List.Item>
                    <List.Item>
                      <Text fw={600}>Instant Creation:</Text> If you don't have
                      one, we'll help you generate one locally in your browser. They are yours
                      to keep, export, and own forever.
                    </List.Item>
                  </List>

                  <div>
                    <Title order={4} mb="sm">
                      Signing the Intent
                    </Title>
                    <Text c="dimmed">
                      We don't "host" or read your documents. We sign the{" "}
                      <strong>cryptographic intent</strong>, a mathematical
                      link between your identity and the document's unique
                      fingerprint. Every signature is now automatically
                      protected by a{" "}
                      <strong>verifiable network attestation</strong> of time
                      and location.
                    </Text>
                  </div>
                </Stack>
              </div>
            </div>

            {/* Encrypted Key Vault */}
            <div className="feature-block">
              <div className="feature-card premium-wrapper">
                <Group gap="md" mb="md">
                  <IconShieldCheck
                    className="section-icon"
                    style={{ width: rem(28), height: rem(28) }}
                  />
                  <Title order={2} className="section-title">
                    Your Local Key Vault
                  </Title>
                </Group>
                <Stack gap="lg">
                  <div>
                    <Title order={3} mb="sm">
                      Your Keys. Encrypted at Rest. Always.
                    </Title>
                    <Text c="dimmed">
                      Your signing keys are stored in an{" "}
                      <strong>AES-256 encrypted vault</strong> that lives
                      entirely on your physical device. Not in a cookie. Not in plain
                      text. In a hardware-backed, password-protected strongbox.
                    </Text>
                  </div>

                  <List spacing="md" className="feature-list">
                    <List.Item>
                      <Text fw={600}>Biometric Unlock:</Text> Use Touch ID, Face
                      ID, or Windows Hello to unlock your vault instant, no
                      password needed.
                    </List.Item>
                    <List.Item>
                      <Text fw={600}>Password Fallback:</Text> Prefer a classic
                      approach? Set a master password. We derive your encryption
                      key with 100,000 rounds of PBKDF2, making brute-force
                      impractical.
                    </List.Item>
                    <List.Item>
                      <Text fw={600}>Auto-Lock:</Text> Your vault automatically
                      locks after 30 minutes of inactivity. If you walk away,
                      your keys are safe.
                    </List.Item>
                    <List.Item>
                      <Text fw={600}>Zero Knowledge:</Text> The encryption key
                      is non-extractable and exists only in memory while
                      unlocked. Even a compromised script can't read the raw
                      key.
                    </List.Item>
                  </List>
                </Stack>
              </div>
            </div>
            <div className="feature-block">
              <div className="feature-card premium-wrapper">
                <Group gap="md" mb="md">
                  <IconScale
                    className="section-icon"
                    style={{ width: rem(28), height: rem(28) }}
                  />
                  <Title order={2} className="section-title">
                    Choose Your Level of Trust
                  </Title>
                </Group>

                <Text c="dimmed" mb="xl" size="lg">
                  We offer a free, unlimited tier for personal privacy, a
                  Pro tier for professional legal weight, and Enterprise solutions for scale.
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl" mt="xl">
                  {/* Sovereign */}
                  <Card withBorder radius="md" p="xl" className="pricing-card">
                    <Stack justify="space-between" h="100%">
                      <Stack>
                        <Group justify="space-between">
                          <Title order={3}>Sovereign</Title>
                          <Badge color="gray">Free</Badge>
                        </Group>
                        <Text c="dimmed" size="sm" mb="md">Personal Privacy / Tech Teams</Text>

                        <Text fw={700} fz="h2" mb="md">$0 <Text span fz="sm" c="dimmed">Forever</Text></Text>

                        <List spacing="sm" size="sm" center>
                          <List.Item icon={<ThemeIcon color="teal" size={24} radius="xl"><IconCircleCheckFilled size={16} /></ThemeIcon>}>Local PGP Key Identity</List.Item>
                          <List.Item icon={<ThemeIcon color="teal" size={24} radius="xl"><IconCircleCheckFilled size={16} /></ThemeIcon>}>Public Keyserver Sync</List.Item>
                          <List.Item icon={<ThemeIcon color="teal" size={24} radius="xl"><IconCircleCheckFilled size={16} /></ThemeIcon>}>Cloudflare-Backed Witness</List.Item>
                          <List.Item icon={<ThemeIcon color="teal" size={24} radius="xl"><IconCircleCheckFilled size={16} /></ThemeIcon>}>Embedded Metadata (SES)</List.Item>
                          <List.Item icon={<ThemeIcon color="teal" size={24} radius="xl"><IconCircleCheckFilled size={16} /></ThemeIcon>}>Manual PDF Distribution</List.Item>
                        </List>
                      </Stack>
                    </Stack>
                  </Card>

                  {/* Pro */}
                  <Card withBorder radius="md" p="xl" className="pricing-card pricing-card-pro" shadow="md">
                    <Stack justify="space-between" h="100%">
                      <Stack>
                        <Group justify="space-between">
                          <Title order={3}>Pro</Title>
                          <Badge color="#0d7a6b">Coming Soon</Badge>
                        </Group>
                        <Text c="dimmed" size="sm" mb="md">Professional Legal Contracts</Text>

                        <Text fw={700} fz="xl" mb="md">Pay-per-envelope <Text span fz="sm" c="dimmed">+ signer</Text></Text>

                        <Text size="sm" fw={600} mt="sm">All Sovereign features, plus:</Text>
                        <List spacing="sm" size="sm" center>
                          <List.Item icon={<ThemeIcon color="blue" size={24} radius="xl"><IconLockCheck size={16} /></ThemeIcon>}>PGP + Identity Verification</List.Item>
                          <List.Item icon={<ThemeIcon color="blue" size={24} radius="xl"><IconClockRecord size={16} /></ThemeIcon>}><strong>RFC 3161 Certified</strong></List.Item>
                          <List.Item icon={<ThemeIcon color="blue" size={24} radius="xl"><IconCertificate size={16} /></ThemeIcon>}><strong>PAdES Compat (eIDAS AES)</strong></List.Item>
                          <List.Item icon={<ThemeIcon color="blue" size={24} radius="xl"><IconMailCheck size={16} /></ThemeIcon>}>
                            <Tooltip label="When ready to sign, we send your document for you. Your file passes by our server in transit only ever in memory, we never read it or save it" multiline w={300}>
                              <Text component="span" style={{ borderBottom: "1px dashed #ccc", cursor: "help" }}>Email Envelopes</Text>
                            </Tooltip>
                          </List.Item>
                        </List>
                      </Stack>
                    </Stack>
                  </Card>

                  {/* Enterprise */}
                  <Card withBorder radius="md" p="xl" className="pricing-card">
                    <Stack justify="space-between" h="100%">
                      <Stack>
                        <Group justify="space-between">
                          <Title order={3}>Enterprise</Title>
                        </Group>
                        <Text c="dimmed" size="sm" mb="md">Large Organizations</Text>

                        <Text fw={700} fz="xl" mb="md">Custom Pricing</Text>

                        <Text size="sm" fw={600} mt="sm">All Pro features, plus:</Text>
                        <List spacing="sm" size="sm" center>
                          <List.Item icon={<ThemeIcon color="grape" size={24} radius="xl"><IconBriefcase size={16} /></ThemeIcon>}>Enterprise SSO Integration</List.Item>
                          <List.Item icon={<ThemeIcon color="grape" size={24} radius="xl"><IconServer size={16} /></ThemeIcon>}>Custom Corporate Keyserver</List.Item>
                          <List.Item icon={<ThemeIcon color="grape" size={24} radius="xl"><IconCircleCheckFilled size={16} /></ThemeIcon>}>Fully Whitelabeled Branding</List.Item>
                        </List>
                      </Stack>
                    </Stack>
                  </Card>
                </SimpleGrid>
              </div>
            </div>

            {/* Flexible Funding */}
            <div className="feature-block">
              <div className="feature-card premium-wrapper">
                <Group gap="md" mb="md">
                  <IconKey
                    className="section-icon"
                    style={{ width: rem(28), height: rem(28) }}
                  />
                  <Title order={2} className="section-title">
                    Flexible Funding: Who Pays?
                  </Title>
                </Group>
                <Stack gap="lg">
                  <Text c="dimmed" size="lg">
                    Signed+ is the first platform that allows the{" "}
                    <strong>Signer</strong> to decide how much trust they want
                    to put behind their name.
                  </Text>

                  <List spacing="md" className="feature-list">
                    <List.Item>
                      <Text fw={600}>The Business Pre-Paid:</Text> A document
                      creator can pre-pay for "Verified" signatures for all
                      recipients. This ensures every signature comes back with
                      an RFC 3161 timestamp and an identity audit trail.
                    </List.Item>
                    <List.Item>
                      <Text fw={600}>The Signer Upgrade:</Text> If a document is
                      sent for a "Sovereign" signature, the signer can choose to{" "}
                      <strong>personally upgrade</strong> their signature to
                      "Verified" at the moment of signing. This allows
                      freelancers and professionals to put a "Gold Seal" on
                      their work, regardless of what the sender requested.
                    </List.Item>
                  </List>
                </Stack>
              </div>
            </div>

            {/* Why Free */}
            <div className="feature-block">
              <div className="feature-card premium-wrapper">
                <Group gap="md" mb="md">
                  <IconClock
                    className="section-icon"
                    style={{ width: rem(28), height: rem(28) }}
                  />
                  <Title order={2} className="section-title">
                    Why is Signed+ Free?
                  </Title>
                </Group>
                <Stack gap="lg">
                  <Text c="dimmed" size="lg">
                    We believe everyone has a right to own their signature. Our base{" "}
                    <strong>Sovereign</strong> tier is 100% free forever. We keep it
                    free through two transparent methods:
                  </Text>

                  <List spacing="md" type="ordered" className="feature-list">
                    <List.Item>
                      <Text fw={600}>Paid Premium Features:</Text> We charge businesses
                      and power users for optional "High-Assurance" extra features (like
                      automated delivery and Official RFC 3161 Trusted Legal Timestamps).
                      Upgrading is never required, but those who do pay help subsidize the
                      free tier for everyone else.
                    </List.Item>
                    <List.Item>
                      <Text fw={600}>Privacy-Respecting Sponsors:</Text> You might
                      occasionally see a brief, unobtrusive message from one of our security
                      partners during the processing phase. We only partner with
                      companies that meet our strict data-privacy standards.
                    </List.Item>
                  </List>
                </Stack>
              </div>
            </div>

            {/* FAQ */}
            <div className="feature-block">
              <Title order={2} className="section-title" mb="xl">
                Frequently Asked Questions
              </Title>

              <Stack gap="md">
                <div className="faq-card">
                  <Text fw={600} mb="xs">
                    Can I upgrade a document someone else sent me?
                  </Text>
                  <Text c="dimmed">
                    Yes. If you receive a document and want to ensure it has the highest
                    legal standing, you can choose to verify your own identity and add an
                    RFC 3161 timestamp even if the sender didn't pay for it.
                  </Text>
                </div>

                <div className="faq-card">
                  <Text fw={600} mb="xs">
                    Is it really eIDAS compliant?
                  </Text>
                  <Text c="dimmed">
                    Yes. Our architecture is eIDAS compliant by design. The
                    Sovereign tier provides a Simple Electronic Signature (SES).
                    Our Verified tier adds the identity-linking required for
                    Advanced Electronic Signatures (AES).
                  </Text>
                </div>

                <div className="faq-card">
                  <Text fw={600} mb="xs">
                    What happens if Signed+ goes offline?
                  </Text>
                  <Text c="dimmed">
                    Your signatures remain valid. Because we embed the proof and
                    your public keys are on global registries, any standard PGP
                    tool can verify your document without us.
                  </Text>
                </div>
              </Stack>
            </div>
          </Stack>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="cta-section">
        <Container size="md">
          <Stack gap="xl" align="center">
            <Title order={2} className="cta-title">
              Stop giving away your data to sign a page.
            </Title>
            <Button size="xl" className="cta-button" onClick={onGetStarted}>
              Switch to Signed+ Today
            </Button>
          </Stack>
        </Container>
      </section>

      {/* Footer / Server Key details */}
      <footer className="landing-footer" style={{ borderTop: "1px solid #e9ecef", padding: "3rem 0", backgroundColor: "#f8f9fa", marginTop: "4rem" }}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
            <Stack gap="xs">
              <Text fw={700} size="md">Signed+</Text>
              <Text size="xs" c="dimmed" style={{ maxWidth: 360 }}>
                A Zero-Trust Ephemeral Key Generation (ZTEKG) platform.
                Documents are never stored; verification travels in the PDF itself.
              </Text>
              <Text size="11px" c="dimmed" mt="md">
                © {new Date().getFullYear()} Signed+. All rights reserved.
              </Text>
            </Stack>

            <Stack gap="xs" style={{ minWidth: 280 }}>
              <Text fw={700} size="sm" c="blue">Current Verification Authority</Text>
              {serverKey ? (
                <div style={{ background: "rgba(34,139,230,0.04)", border: "1px solid rgba(34,139,230,0.15)", borderRadius: "8px", padding: "12px" }}>
                  <Group justify="space-between" mb="xs">
                    <Text size="xs" fw={700} c="blue.8">Server Signing Key</Text>
                    <Badge size="xs" color="blue" variant="light">{serverKey.keyId}</Badge>
                  </Group>
                  <Text size="10px" c="dimmed" style={{ fontFamily: "monospace" }}>
                    Type: ECDSA P-256 (NIST P-256)
                  </Text>
                  <Text size="10px" c="dimmed" style={{ fontFamily: "monospace" }} truncate>
                    X: {String((serverKey.publicKey as any).x || "")}
                  </Text>
                  <Text size="10px" c="dimmed" style={{ fontFamily: "monospace" }} truncate>
                    Y: {String((serverKey.publicKey as any).y || "")}
                  </Text>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Loader size="xs" />
                  <Text size="xs" c="dimmed">Querying active signing authority...</Text>
                </div>
              )}
              <Text size="10px" c="dimmed" mt="xs">
                To guarantee long-term verifiability even if Signed+ goes offline, public keys are mirrored dynamically to our authoritative archives:
              </Text>
              <Group gap="xs" mt="xs">
                {/* <Button
                  component="a"
                  href="https://keys.signed.plus/manifest.json"
                  target="_blank"
                  variant="subtle"
                  size="compact-xs"
                >
                  R2 Public CDN
                </Button> */}
                <Button
                  component="a"
                  href="https://github.com/andersdn/signedplus-public-keys"
                  target="_blank"
                  variant="subtle"
                  size="compact-xs"
                >
                  Public Key GitHub Registry
                </Button>
              </Group>
            </Stack>
          </SimpleGrid>
        </Container>
      </footer>
    </div>
  );
}
